<script setup>
import { computed, reactive, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  scenario: {
    type: Object,
    default: null,
  },
  templateMeta: {
    type: Object,
    default: null,
  },
  fieldOptions: {
    type: Array,
    default: () => [],
  },
  isMipEnabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'save', 'manage-fields']);

const state = reactive({
  id: null,
  title: '',
  enabled: true,
  trigger_conditions: [],
  selected_field_keys: [],
  required_field_keys: [],
  task_config: {},
});

watch(
  () => props.scenario,
  scenario => {
    if (!scenario) return;

    state.id = scenario.id;
    state.title = scenario.title || props.templateMeta?.title || '';
    state.enabled = scenario.enabled;
    state.trigger_conditions = [...(scenario.trigger_conditions || [])];
    state.selected_field_keys = [...(scenario.selected_field_keys || [])];
    state.required_field_keys = [...(scenario.required_field_keys || [])];
    state.task_config = { ...(scenario.task_config || {}) };
  },
  { immediate: true }
);

const close = () => emit('update:modelValue', false);

const triggerText = computed({
  get: () => state.trigger_conditions.join('\n'),
  set: value => {
    state.trigger_conditions = value
      .split(/\n|,|，/)
      .map(item => item.trim())
      .filter(Boolean);
  },
});

const closingFieldLabel = computed(() => {
  switch (props.templateMeta?.templateKey) {
    case 'complaint_soothing':
      return '致歉消息';
    case 'channel_partnership':
      return '转人工说明';
    default:
      return '收集完成提示';
  }
});

const openingFieldLabel = computed(() => {
  switch (props.templateMeta?.templateKey) {
    case 'complaint_soothing':
      return '安抚开场';
    default:
      return '开场说明';
  }
});

const saveActionLabel = computed(() =>
  props.isMipEnabled ? '保存线索到MIP' : '保存信息到联系人'
);

const saveActionDescription = computed(() =>
  props.isMipEnabled
    ? '用户提供字段后，AI 会将线索信息保存到 MIP'
    : '用户提供字段后，AI 会将信息保存到联系人'
);

const closingMessageModel = computed({
  get: () =>
    props.templateMeta?.hasHandoff
      ? state.task_config.handoff_message
      : state.task_config.completion_message,
  set: value => {
    if (props.templateMeta?.hasHandoff) {
      state.task_config.handoff_message = value;
    } else {
      state.task_config.completion_message = value;
    }
  },
});

const toggleField = key => {
  if (state.selected_field_keys.includes(key)) {
    state.selected_field_keys = state.selected_field_keys.filter(item => item !== key);
    state.required_field_keys = state.required_field_keys.filter(item => item !== key);
  } else {
    state.selected_field_keys = [...state.selected_field_keys, key];
  }
};

const toggleRequiredField = key => {
  if (!state.selected_field_keys.includes(key)) return;

  if (state.required_field_keys.includes(key)) {
    state.required_field_keys = state.required_field_keys.filter(item => item !== key);
  } else {
    state.required_field_keys = [...state.required_field_keys, key];
  }
};

const save = () => {
  if (
    ['trial_signup', 'channel_partnership'].includes(props.templateMeta?.templateKey)
  ) {
    if (state.selected_field_keys.length === 0) {
      useAlert('请至少选择一个收集字段');
      return;
    }

    if (state.required_field_keys.length === 0) {
      useAlert('请至少设置一个必收集字段');
      return;
    }
  }

  emit('save', {
    id: state.id,
    title: state.title.trim(),
    enabled: state.enabled,
    trigger_conditions: state.trigger_conditions,
    selected_field_keys: state.selected_field_keys,
    required_field_keys: state.required_field_keys,
    task_config: state.task_config,
  });
  close();
};
</script>

<template>
  <Teleport to="body">
    <transition
      enter-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="modelValue"
        class="fixed inset-0 bg-black/50 z-[9998]"
        @click="close"
      />
    </transition>

    <transition
      enter-active-class="transition-transform duration-300 ease-out"
      enter-from-class="translate-x-full"
      enter-to-class="translate-x-0"
      leave-active-class="transition-transform duration-200 ease-in"
      leave-from-class="translate-x-0"
      leave-to-class="translate-x-full"
    >
      <div
        v-if="modelValue && scenario && templateMeta"
        class="fixed right-0 top-0 h-full w-[36rem] max-w-[92vw] bg-n-solid-1 dark:bg-n-slate-900 shadow-2xl z-[9999] flex flex-col"
      >
        <div
          class="px-6 py-4 border-b border-n-slate-6 dark:border-n-slate-700"
        >
          <div class="flex items-center justify-between gap-4">
            <div class="min-w-0">
              <h3 class="text-lg font-medium text-n-slate-12">编辑AI线索</h3>
            </div>
            <button
              class="p-1.5 size-8 rounded-md hover:bg-n-slate-2 dark:hover:bg-n-slate-800 transition-colors flex items-center justify-center"
              @click="close"
            >
              <Icon icon="i-lucide-x" class="size-5 text-n-slate-11" />
            </button>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto px-6 py-5">
          <div class="flex flex-col gap-5">
            <div class="flex flex-col gap-3">
              <div class="text-lg text-n-slate-12">基本信息</div>
              <section class="flex flex-col gap-3">
                <div class="text-sm text-n-slate-12">名称</div>
                <Input v-model="state.title" placeholder="请输入AI线索流程名称" />
                <Switch v-model="state.enabled" label="启用此AI线索" />
              </section>
            </div>

            <div class="flex flex-col gap-3">
              <div class="text-lg text-n-slate-12">处理流程</div>

              <section class="flex flex-col gap-3">
                <div class="flex flex-col gap-1">
                  <div class="text-sm text-n-slate-12">触发条件</div>
                  <div class="text-xs text-n-slate-10">
                    描述用户在什么情况下会触发这个场景，AI 会意图判断
                  </div>
                </div>
                <TextArea
                  v-model="triggerText"
                  placeholder="简单描述什么场景让用户进入流程，关键词和简短描述都可"
                  show-character-count
                />
              </section>

              <section class="flex flex-col gap-3">
                <div class="text-sm text-n-slate-12">{{ openingFieldLabel }}</div>
                <TextArea
                  v-model="state.task_config.opening_message"
                  :placeholder="`请输入${openingFieldLabel}`"
                  show-character-count
                />

                <div
                  v-if="templateMeta.templateKey === 'complaint_soothing'"
                  class="flex flex-col gap-3"
                >
                  <div class="text-sm text-n-slate-12">询问原因</div>
                  <Switch
                    v-model="state.task_config.ask_reason"
                    label="是否询问原因"
                  />
                </div>
              </section>

              <section
                v-if="templateMeta.usesFields"
                class="flex flex-col gap-3"
              >
                <div class="flex items-center justify-between gap-3">
                  <div>
                    <div class="text-sm text-n-slate-12">收集字段</div>
                    <div class="text-xs text-n-slate-10 mt-1">
                      为当前AI线索选择需要收集的字段
                    </div>
                  </div>
                  <Button
                    label="管理字段"
                    xs
                    ghost
                    slate
                    @click="emit('manage-fields')"
                  />
                </div>

                <div
                  v-if="fieldOptions.length"
                  class="grid grid-cols-2 gap-2"
                >
                  <label
                    v-for="field in fieldOptions"
                    :key="field.key"
                    class="flex items-start gap-2 rounded-xl border px-3 py-3 cursor-pointer transition-colors"
                    :class="
                      state.selected_field_keys.includes(field.key)
                        ? 'border-n-brand bg-n-brand/5 dark:border-n-brand dark:bg-n-brand/10'
                        : 'border-transparent bg-n-slate-2/80 dark:bg-n-slate-7/50'
                    "
                  >
                  <Checkbox
                    :model-value="state.selected_field_keys.includes(field.key)"
                    @update:model-value="toggleField(field.key)"
                  />
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center justify-between gap-2">
                      <div class="text-sm text-n-slate-12">
                        {{ field.label || field.key }}
                      </div>
                        <div
                          v-if="state.selected_field_keys.includes(field.key)"
                          class="flex items-center gap-2 text-xs text-n-slate-10"
                          @click.stop
                        >
                          <span>必收集</span>
                          <Switch
                            :model-value="state.required_field_keys.includes(field.key)"
                            @update:model-value="toggleRequiredField(field.key)"
                          />
                        </div>
                      </div>
                      <div
                        v-if="field.description"
                        class="text-xs text-n-slate-10 mt-0.5"
                      >
                        {{ field.description }}
                      </div>
                    </div>
                  </label>
                </div>
                <div v-else class="text-xs text-amber-600 dark:text-amber-400">
                  当前没有可选字段，请先配置字段池
                </div>
              </section>

              <section
                v-if="templateMeta.usesFields"
                class="flex flex-col gap-3"
              >
                <div class="text-sm text-n-slate-12">{{ saveActionLabel }}</div>
                <div class="text-sm text-n-slate-10">
                  {{ saveActionDescription }}
                </div>
              </section>

              <section
                class="flex flex-col gap-3"
              >
                <div class="text-sm text-n-slate-12">{{ closingFieldLabel }}</div>
                <TextArea
                  v-model="closingMessageModel"
                  :placeholder="`请输入${closingFieldLabel}`"
                  show-character-count
                />
              </section>

              <section
                v-if="templateMeta.hasHandoff"
                class="flex flex-col gap-3"
              >
                <div class="text-sm text-n-slate-12">转交人工</div>
                <div class="text-sm text-n-slate-10">
                  满足当前流程后，AI 会自动转交人工继续跟进
                </div>
              </section>
            </div>
          </div>
        </div>

        <div
          class="flex items-center gap-3 px-6 py-4 border-t border-n-slate-6 dark:border-n-slate-700 bg-white/90 dark:bg-n-slate-900/90"
        >
          <Button faded slate label="取消" class="flex-1" @click="close" />
          <Button label="保存修改" class="flex-1" @click="save" />
        </div>
      </div>
    </transition>
  </Teleport>
</template>
